import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { addToChainAfter, Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";

import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    DispatchUpdateVariantSelection,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Parameter {
    variantTraitId?: string;
    traitValueId?: string;
    productId: string;
    styledOption?: string;
}

interface Props {
    variantSelection: SafeDictionary<string>;
    variantSelectionCompleted: boolean;
    selectedProductId?: string;
    productInfo?: ProductInfo;
    inventoryLoaded?: true;
}

type HandlerType = Handler<Parameter, Props>;

export const LoadRealTimePricingDataAfterAllVariantSelection: HandlerType = props => {
    const { productInfo, selectedProductId: productId } = props;
    if (!productInfo || !productId) {
        return;
    }
    if (props.variantSelectionCompleted) {
        props.dispatch(
            loadRealTimePricing({
                productPriceParameters: [productInfo],
                onSuccess: realTimePricing => {
                    const pricing = realTimePricing.realTimePricingResults!.find(
                        o => o.productId === productInfo.productId,
                    );
                    if (pricing) {
                        props.dispatch({
                            type: "Pages/ProductDetails/CompleteLoadRealTimePricing",
                            pricing,
                        });
                    } else {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimePricing",
                            productId,
                        });
                    }
                },
                onError: () => {
                    props.dispatch({
                        type: "Pages/ProductDetails/FailedLoadRealTimePricing",
                        productId,
                    });
                },
                onComplete(realTimePricingProps) {
                    if (realTimePricingProps.apiResult) {
                        this.onSuccess?.(realTimePricingProps.apiResult);
                    } else if (realTimePricingProps.error) {
                        this.onError?.(realTimePricingProps.error);
                    }
                },
            }),
        );
    }
};
function getVariantSelection(product?: ProductModel, variantChildren?: ProductModel[], styledOption?: string) {
    const variantSelection: SafeDictionary<string> = {};
    if (!product?.isVariantParent || !variantChildren) {
        return variantSelection;
    }

    if (styledOption || product.defaultChildProductId) {
        const variantChild = styledOption
            ? variantChildren.find(o => o.productNumber.toLowerCase() === styledOption.toLowerCase())
            : variantChildren.find(o => o.id === product.defaultChildProductId);

        variantChild?.childTraitValues?.forEach(o => {
            variantSelection[o.styleTraitId] = o.id;
        });

        if (Object.keys(variantSelection).length > 0) {
            return variantSelection;
        }
    }

    product.variantTraits?.forEach(variantTrait => {
        const defaultVariantValue = variantTrait.traitValues?.find(traitValue => traitValue.isDefault);
        variantSelection[variantTrait.id] = defaultVariantValue?.id ?? "";
    });

    return variantSelection;
}

export const LoadRealTimeInventoryDataAfterAllVariantSelection: HandlerType = props => {
    let variantChildren = getVariantChildrenDataView(props.getState(), props.parameter.productId).value!;

    const state = props.getState();
    const product = getProductState(state, props.selectedProductId).value;

    if (
        !product ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    const productId = props.selectedProductId as string;
    if (props.variantSelectionCompleted) {
        // Modify here to only include the selected product ID (instead of all variants)
        props.dispatch(
            loadRealTimeInventory({
                productIds: [productId], // Only the selected product
                onComplete: realTimeInventoryProps => {
                    const realTimeInventory = realTimeInventoryProps?.apiResult;

                    if (realTimeInventoryProps?.error) {
                        if (product) {
                            props.dispatch({
                                type: "Pages/ProductDetails/FailedLoadRealTimeInventory",
                                productId: product.id,
                            });
                        }
                    } else if (realTimeInventory) {
                        if (realTimeInventory.realTimeInventoryResults && variantChildren && product) {
                            const discontinued: SafeDictionary<boolean> = {};
                            for (const result of realTimeInventory.realTimeInventoryResults) {
                                if (!result.qtyOnHand) {
                                    discontinued[result.productId] = true;
                                }
                            }

                            // Filter out discontinued variants if any
                            const initialCount = variantChildren.length;
                            variantChildren = variantChildren.filter(
                                o => !o.isDiscontinued || (o.isDiscontinued && !discontinued[o.id]),
                            );

                            // If any variants were removed, update the data view
                            if (initialCount !== variantChildren.length) {
                                const variantChildrenDataView = getVariantChildrenDataView(
                                    props.getState(),
                                    product.id,
                                );
                                const { value, ...otherProps } = variantChildrenDataView;
                                props.dispatch({
                                    type: "Data/Products/ReplaceDataView",
                                    parameter: {
                                        productId: product.id,
                                        variantChildren: true,
                                    },
                                    dataView: {
                                        value: variantChildren,
                                        ...otherProps,
                                    },
                                });

                                const productInfo =
                                    props.getState().pages.productDetails.productInfosById?.[product.id] ??
                                    createFromProduct(getProductState(props.getState(), product.id).value ?? product);

                                props.dispatch({
                                    type: "Pages/ProductDetails/UpdateVariantSelection",
                                    variantSelection: getVariantSelection(
                                        product,
                                        variantChildren,
                                        props.parameter.styledOption,
                                    ),
                                    variantSelectionCompleted: false,
                                    selectedProductInfo: productInfo,
                                });
                            }
                        }

                        props.dispatch({
                            type: "Pages/ProductDetails/CompleteLoadRealTimeInventory",
                            realTimeInventory,
                        });
                    }

                    props.inventoryLoaded = true;
                },
            }),
        );
    }
};

addToChainAfter(chain, DispatchUpdateVariantSelection, LoadRealTimePricingDataAfterAllVariantSelection);
addToChainAfter(chain, DispatchUpdateVariantSelection, LoadRealTimeInventoryDataAfterAllVariantSelection);